"use client";

import { useState, useEffect, useMemo, type CSSProperties } from "react";
import { motion, useReducedMotion } from "framer-motion";

interface AnimatedBackgroundProps {
  /** ścieżka z /public, np. "/webp1.webp" */
  backgroundImage?: string;
  /** ile drobnych kulek w warstwie ambient (domyślnie 140) */
  ambientCount?: number;
}

export default function AnimatedBackground({
  backgroundImage = "/webp1.webp",
  ambientCount = 0, // było 80 – lekko zwiększone
}: AnimatedBackgroundProps) {
  const [mounted, setMounted] = useState(false);
  const prefersReducedMotion = useReducedMotion();

  // Stabilne pozycje (bez randomu na SSR)
  const generatePosition = (index: number, isX: boolean) => {
    const positions = [
      { x: 5, y: 20 }, { x: 95, y: 15 }, { x: 25, y: 80 },
      { x: 70, y: 90 }, { x: 40, y: 10 }, { x: 60, y: 45 },
      { x: 15, y: 60 }, { x: 85, y: 70 }, { x: 35, y: 95 },
      { x: 55, y: 30 }, { x: 80, y: 40 }, { x: 20, y: 85 },
      { x: 45, y: 65 }, { x: 75, y: 75 }, { x: 30, y: 35 },
      { x: 10, y: 50 }, { x: 90, y: 25 }, { x: 50, y: 55 },
      { x: 65, y: 20 }, { x: 25, y: 70 }, { x: 85, y: 85 },
      { x: 40, y: 40 }, { x: 15, y: 30 }, { x: 70, y: 60 },
      { x: 95, y: 45 }, { x: 5, y: 75 }, { x: 60, y: 15 },
      { x: 35, y: 50 }, { x: 80, y: 80 }, { x: 20, y: 25 },
    ];
    return isX ? positions[index]?.x ?? 50 : positions[index]?.y ?? 50;
  };

  const generateTargetPosition = (index: number, isX: boolean) => {
    const targets = [
      { x: 85, y: 60 }, { x: 20, y: 85 }, { x: 90, y: 35 },
      { x: 10, y: 45 }, { x: 60, y: 95 }, { x: 30, y: 25 },
      { x: 75, y: 70 }, { x: 45, y: 15 }, { x: 70, y: 55 },
      { x: 35, y: 80 }, { x: 25, y: 40 }, { x: 95, y: 90 },
      { x: 55, y: 20 }, { x: 15, y: 95 }, { x: 65, y: 50 },
      { x: 80, y: 30 }, { x: 40, y: 85 }, { x: 5, y: 65 },
      { x: 50, y: 10 }, { x: 75, y: 45 }, { x: 30, y: 70 },
      { x: 60, y: 60 }, { x: 90, y: 75 }, { x: 20, y: 40 },
      { x: 45, y: 90 }, { x: 85, y: 20 }, { x: 35, y: 35 },
      { x: 65, y: 80 }, { x: 10, y: 55 }, { x: 55, y: 65 },
    ];
    return isX ? targets[index]?.x ?? 50 : targets[index]?.y ?? 50;
  };

  useEffect(() => setMounted(true), []);

  // Ambient – drobne niebieskie, bardzo subtelne
  const ambientParticles = useMemo(() => {
    const count = Math.min(Math.max(ambientCount, 0), 300); // bezpieczny limit
    return Array.from({ length: count }, (_, i) => {
      const baseLeft = ((i * 37) % 100) + (i % 2 === 0 ? 0.25 : 0.75);
      const baseTop = ((i * 53) % 100) + (i % 3 === 0 ? 0.25 : 0.75);
      const size = 2 + ((i * 7) % 4) * 0.4; // 2.0 - 3.6 px
      const opacity = 0.18 + ((i % 10) * 0.015); // ~0.18 - 0.315
      const drift = 6 + (i % 6); // px
      const duration = 9 + (i % 10); // s
      const delay = (i % 20) * 0.25;
      return { i, baseLeft, baseTop, size, opacity, drift, duration, delay };
    });
  }, [ambientCount]);

  return (
    <div className="fixed inset-0 pointer-events-none z-0">
      {/* BACKGROUND IMAGE — bez przyciemnienia */}
      <div className="absolute inset-0 -z-10">
        <div
          className="absolute inset-0"
          style={{
            backgroundImage: `url(${backgroundImage})`,
            backgroundSize: "cover",
            backgroundPosition: "center",
            backgroundRepeat: "no-repeat",
            filter: "none",
          }}
        />
      </div>

      {/* RADIAL OVERLAYS — bez czarnej winiety */}
      <div
        className="absolute inset-0 z-10"
        style={{
          backgroundImage: `
            radial-gradient(1200px 600px at 50% 20%, rgba(34,211,238,0.10), transparent 60%),
            radial-gradient(900px 700px at 50% 50%, rgba(16,185,129,0.08), transparent 65%)
          `,
        }}
      />

      {/* GLOWING BLOBS — subtelniejsze */}
      {!prefersReducedMotion && (
        <>
          <motion.div
            className="absolute top-20 left-20 w-96 h-96 rounded-full blur-3xl z-20"
            style={{ backgroundColor: "rgba(34,211,238,0.09)" }}
            animate={{ scale: [1, 1.2, 1], x: [0, 50, 0], y: [0, -30, 0] }}
            transition={{ duration: 20, repeat: Infinity }}
          />
          <motion.div
            className="absolute bottom-20 right-20 w-96 h-96 rounded-full blur-3xl z-20"
            style={{ backgroundColor: "rgba(20,184,166,0.09)" }}
            animate={{ scale: [1.2, 1, 1.2], x: [0, -50, 0], y: [0, 30, 0] }}
            transition={{ duration: 25, repeat: Infinity }}
          />
        </>
      )}

      {/* GRID OVERLAY — delikatniejsza */}
      <div className="absolute inset-0 opacity-5 z-20">
        <div
          className="absolute inset-0"
          style={{
            backgroundImage: `
              linear-gradient(90deg, rgba(45,212,191,0.18) 1px, transparent 1px),
              linear-gradient(rgba(45,212,191,0.16) 1px, transparent 1px)
            `,
            backgroundSize: "50px 50px",
          }}
        />
      </div>

      {/* NETWORK NODES */}
      <div className="absolute inset-0 overflow-hidden z-30">
        {[...Array(0)].map((_, i) => {
          const initialLeft = `${generatePosition(i, true)}%`;
          const initialTop = `${generatePosition(i, false)}%`;
          const targetLeft = `${generateTargetPosition(i, true)}%`;
          const targetTop = `${generateTargetPosition(i, false)}%`;
          const duration = 20 + (i % 10) * 2;

          if (!mounted || prefersReducedMotion) {
            return (
              <div
                key={`node-${i}`}
                className="absolute rounded-full"
                style={{
                  width: 2,
                  height: 2,
                  left: initialLeft,
                  top: initialTop,
                  backgroundColor: "rgba(34,211,238,0.45)",
                  boxShadow: "0 0 6px rgba(34,211,238,0.6)",
                }}
              />
            );
          }

          return (
            <motion.div
              key={`node-${i}`}
              className="absolute rounded-full"
              style={{
                width: 2,
                height: 2,
                backgroundColor: "rgba(34,211,238,0.45)",
                boxShadow: "0 0 6px rgba(34,211,238,0.6)",
              }}
              initial={{ left: initialLeft, top: initialTop }}
              animate={{ left: targetLeft, top: targetTop }}
              transition={{
                duration,
                repeat: Infinity,
                repeatType: "reverse",
                ease: "linear",
              }}
            />
          );
        })}
      </div>

      {/* AMBIENT BLUE — drobne, “żyjące” kuleczki */}
      <div className="absolute inset-0 z-40">
        {ambientParticles.map((p) => {
          const baseStyle: CSSProperties = {
            position: "absolute",
            left: `${p.baseLeft}%`,
            top: `${p.baseTop}%`,
            width: `${p.size}px`,
            height: `${p.size}px`,
            borderRadius: "9999px",
            background:
              "radial-gradient(circle at 50% 50%, rgba(96,165,250,0.85), rgba(59,130,246,0.25) 60%, rgba(29,78,216,0.10) 100%)",
            boxShadow: `0 0 ${Math.max(6, p.size * 3)}px rgba(59,130,246,${p.opacity * 0.7})`,
            opacity: p.opacity,
          };

          if (prefersReducedMotion) return <div key={`amb-${p.i}`} style={baseStyle} />;

          return (
            <motion.div
              key={`amb-${p.i}`}
              style={baseStyle}
              animate={{
                x: [0, p.drift, 0, -p.drift, 0],
                y: [0, -p.drift, 0, p.drift, 0],
                scale: [1, 1.12, 1],
              }}
              transition={{
                duration: p.duration,
                repeat: Infinity,
                ease: "easeInOut",
                delay: p.delay,
              }}
            />
          );
        })}
      </div>
    </div>
  );
}
